import { useRef } from "react";
import { NinjaArt } from "../art/NinjaArt";
import { Coins } from "../components/Coins";
import { HomeMenu } from "../components/HomeMenu";
import { Modal } from "../components/Modal";
import { RankCard } from "../components/RankCard";
import { SetDots } from "../components/SetDots";
import { MODE_INFO, type Mode } from "../content/modes";
import { ninjaOfRank } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { variantName } from "../content/variants";
import { availableModes } from "../game/modes";
import { balanceOf, fitFor, kitNames } from "../game/items";
import { fighterFor, isKnown, nextToRecruit } from "../game/roster";
import { frameFor, goldPossible, highestRank, lookFor, setModes, setProgress } from "../game/sets";
import { cardStripes } from "../game/stripes";
import { nextTrial, TOP_RANK_INDEX } from "../game/trials";
import type { SaveState } from "../save/schema";
import "./Home.css";

interface Props {
  save: SaveState;
  mode: Mode;
  onSelectMode: (mode: Mode) => void;
  onTrial: () => void;
  onBattle: () => void;
  onPractice: () => void;
  onLadder: () => void;
  onRoster: () => void;
  onShop: () => void;
  onParents: () => void;
  onOpenCard: (rankIndex: number, mode?: Mode) => void;
  onPlacement: () => void;
  onSkipPlacement: () => void;
  /** Shown when the family has more than one child. */
  onSwitchPlayer?: (() => void) | undefined;
  /** Strands just given the one-off retune award, to celebrate. */
  promoted: readonly Mode[];
  onDismissPromoted: () => void;
}

/** First launch: nothing played yet and the welcome hasn't been answered. */
export function needsWelcome(save: SaveState): boolean {
  return (
    !save.onboarded && save.history.length === 0 && Object.values(save.rank).every((r) => r === 0)
  );
}

/**
 * What the next gold card needs: "Storm Ninja gold. Next: pass the Take
 * trial." Null when gold isn't possible (one strand) or every card is gold.
 */
export function setLine(save: SaveState): { rankIndex: number; text: string } | null {
  const ops = save.settings.ops;
  if (!goldPossible(ops)) return null;
  const target = save.overall + 1;
  if (target > TOP_RANK_INDEX) return { rankIndex: save.overall, text: "Every card is gold!" };
  const missing = setModes(ops)
    .filter((m) => save.rank[m] < target)
    .sort((a, b) => save.rank[a] - save.rank[b]);
  const name = RANKS[target]!.name;
  const first = missing[0];
  if (!first) return { rankIndex: target, text: `${name} gold!` };
  const trial =
    save.rank[first] === target - 1
      ? `pass the ${MODE_INFO[first].label} trial`
      : `reach ${name} in ${MODE_INFO[first].label}`;
  return { rankIndex: target, text: `${name} gold. Next: ${trial}.` };
}

/** "Your best already passes the new Storm Ninja trial in Add!" */
export function promotedText(save: SaveState, promoted: readonly Mode[]): string {
  const parts = promoted.map((m) => `${RANKS[save.rank[m]]!.name} in ${MODE_INFO[m].label}`);
  const list =
    parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}` : (parts[0] ?? "");
  return `The trials have been tuned, and your best scores already pass them. You're now ${list}!`;
}

/**
 * "Next to recruit: Bamboo Sprout, 100 health. Fighting with Seedling, with
 * Swift Sandals and Smoke Bomb." An empty slot is a nudge once the child owns
 * something to fit.
 */
export function battleLine(save: SaveState): string {
  const fighter = fighterFor(save.roster, save.settings.fighter);
  const next = nextToRecruit(save.roster);
  const kit = fitFor(fighter, save.settings.loadout, save.items);
  const carried = kit.active.length + kit.passive.length;
  const slots = fighter.slots.active + fighter.slots.passive;
  const empty = slots - carried;
  const owned = Object.keys(save.items).length;
  const wearing = carried
    ? `, with ${kitNames(kit)}`
    : owned && empty
      ? `, with ${empty} empty slot${empty === 1 ? "" : "s"}`
      : "";
  const who = `Fighting with ${fighter.name}${wearing}.`;
  if (!next) return `Every ninja recruited! ${who}`;
  const name = isKnown(save.rank, save.roster, next) ? next.name : "a mystery ninja";
  return `Next to recruit: ${name}, ${next.health} health. ${who}`;
}

/** The Trials door: the rank held in the strand, "Shadow Cub in Add". */
export function trialDoorText(save: SaveState, mode: Mode): string {
  const label = MODE_INFO[mode].label;
  const held = variantName(mode, RANKS[save.rank[mode]]!.name);
  return nextTrial(mode, save.rank[mode]) ? `${held} in ${label}` : `${label}: Legend!`;
}

/** The Battle door: "Next: Bamboo Sprout, 90 health" or "Every ninja recruited!" */
export function battleDoorText(save: SaveState): string {
  const next = nextToRecruit(save.roster);
  if (!next) return "Every ninja recruited!";
  const name = isKnown(save.rank, save.roster, next) ? next.name : "a mystery ninja";
  return `Next: ${name}, ${next.health} health`;
}

export function Home({
  save,
  mode,
  onSelectMode,
  onTrial,
  onBattle,
  onPractice,
  onLadder,
  onRoster,
  onShop,
  onParents,
  onOpenCard,
  onPlacement,
  onSkipPlacement,
  onSwitchPlayer,
  promoted,
  onDismissPromoted,
}: Props) {
  const modes = availableModes(save.settings.ops);
  const rank = save.rank[mode];
  const anyTrial = modes.some((m) => nextTrial(m, save.rank[m]) !== null);
  const foe = nextToRecruit(save.roster) ?? ninjaOfRank(TOP_RANK_INDEX);
  const foeKnown = isKnown(save.rank, save.roster, foe);
  const welcomeBtn = useRef<HTMLButtonElement>(null);
  const promotedBtn = useRef<HTMLButtonElement>(null);
  // The headline is the highest rank held in any strand; gold and the set line
  // still follow the complete set (save.overall).
  const headline = highestRank(save.rank, save.settings.ops);
  const look = lookFor(save.rank, save.settings.look, headline, setModes(save.settings.ops));
  const set = setLine(save);
  const stripes = cardStripes(save.stripes, mode, rank);
  const welcome = needsWelcome(save);
  return (
    <section className="screen home">
      <header className="home-head">
        <h1 className="title display">Nine Scrolls</h1>
        <p className="hello">
          <span>{save.name ? `Welcome back, ${save.name}` : "Ready for training?"}</span>
          <button
            type="button"
            className="home-coins-btn"
            onClick={onShop}
            aria-label={`${balanceOf(save)} coins. Open the Dojo Shop`}
          >
            <Coins count={balanceOf(save)} className="home-coins" />
          </button>
        </p>
      </header>
      <div className="home-body">
        <div className="home-hero">
          <RankCard
            rankIndex={headline}
            variant={look}
            frame={frameFor(save.sets, headline)}
            kicker="Ninja rank"
            onOpen={(i) => onOpenCard(i, look)}
            className="home-card"
          />
          {set && (
            <div className="set-line">
              <SetDots
                dots={setProgress(save.rank, save.settings.ops, set.rankIndex)}
                onPick={onSelectMode}
              />
              <p>{set.text}</p>
            </div>
          )}
        </div>
        <div className="home-controls">
          {!anyTrial && (
            <p className="home-note">
              You&apos;re a Legend! Dan ranks are coming soon. Keep your skills sharp with practice.
            </p>
          )}
          <div className="doors">
            {anyTrial && (
              <button
                type="button"
                className="door door-trials"
                style={{ "--c": RANKS[rank]!.colour } as React.CSSProperties}
                onClick={onTrial}
                aria-label={`Trials: ${trialDoorText(save, mode)}`}
              >
                <NinjaArt
                  rank={RANKS[rank]!}
                  variant={mode}
                  stripes={stripes}
                  className="door-art"
                />
                <span className="door-name display">Trials</span>
                <span className="door-sub">{trialDoorText(save, mode)}</span>
              </button>
            )}
            <button
              type="button"
              className="door door-battle"
              style={{ "--c": RANKS[foe.rankIndex]!.colour } as React.CSSProperties}
              onClick={onBattle}
              aria-label={`Battle: ${battleDoorText(save)}`}
            >
              <NinjaArt rank={RANKS[foe.rankIndex]!} locked={!foeKnown} className="door-art" />
              <span className="door-name display">Battle</span>
              <span className="door-sub">{battleDoorText(save)}</span>
            </button>
            <button
              type="button"
              className="door door-practice"
              style={{ "--c": RANKS[0]!.colour } as React.CSSProperties}
              onClick={onPractice}
              aria-label="Practice: no clock, no pressure"
            >
              <NinjaArt rank={RANKS[0]!} className="door-art" />
              <span className="door-name display">Practice</span>
              <span className="door-sub">No clock, no pressure</span>
            </button>
          </div>
          <HomeMenu
            items={[
              { icon: "ranks", label: "See all ranks", caption: "Ranks", onClick: onLadder },
              { icon: "ninjas", label: "My ninjas", caption: "Ninjas", onClick: onRoster },
              { icon: "shop", label: "Dojo Shop", caption: "Shop", onClick: onShop },
              ...(onSwitchPlayer
                ? [
                    {
                      icon: "switch" as const,
                      label: "Switch player",
                      caption: "Switch",
                      onClick: onSwitchPlayer,
                    },
                  ]
                : []),
              { icon: "parents", label: "Parents", caption: "Parents", onClick: onParents },
            ]}
          />
        </div>
      </div>
      <Modal open={welcome} label="Welcome" onClose={onSkipPlacement} initialFocus={welcomeBtn}>
        <div className="panel welcome">
          <h2 className="display">New here?</h2>
          <p>
            Do a quick check so you start at the right rank. There&apos;s no clock. Just answer what
            you can.
          </p>
          <button ref={welcomeBtn} type="button" className="big display" onClick={onPlacement}>
            Quick check
          </button>
          <button type="button" className="ghost" onClick={onSkipPlacement}>
            Start at Seedling
          </button>
        </div>
      </Modal>
      <Modal
        open={!welcome && promoted.length > 0}
        label="Rank up"
        onClose={onDismissPromoted}
        initialFocus={promotedBtn}
      >
        <div className="panel welcome promoted">
          <h2 className="display">Your training paid off!</h2>
          <div className="promoted-cards">
            {promoted.map((m) => (
              <NinjaArt
                key={m}
                rank={RANKS[save.rank[m]]!}
                variant={m}
                className="promoted-art reveal"
              />
            ))}
          </div>
          <p>{promotedText(save, promoted)}</p>
          <button
            ref={promotedBtn}
            type="button"
            className="big display"
            onClick={onDismissPromoted}
          >
            Awesome!
          </button>
        </div>
      </Modal>
    </section>
  );
}
